import { useState } from "react";

interface Product {
  id: string;
  title: string;
  unitPrice: number;
  moqUnits: number;
  moqUSD: number;
  containerQty: Record<string, number>;
  image: string;
  category: string;
  customizable: boolean;
}

interface WholesaleFactory {
  id: string;
  name: string;
  country: string;
  region: string;
  flag: string;
  city: string;
  avatar: string;
  banner: string;
  verified: boolean;
  rating: number;
  totalOrders: number;
  yearsActive: number;
  category: string;
  certifications: string[];
  minOrderUSD: number;
  containerTypes: string[];
  responseTime: string;
  sampleAvailable: boolean;
  samplePrice: number;
  sampleLeadTime: string;
  bulkLeadTime: string;
  about: string;
  products: Product[];
}

interface Props {
  factory: WholesaleFactory;
  onSampleOrder: (factory: WholesaleFactory, product: Product) => void;
  onBulkOrder: (factory: WholesaleFactory, product: Product) => void;
}

export default function WholesaleFactoryCard({ factory, onSampleOrder, onBulkOrder }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [activeProduct, setActiveProduct] = useState(factory.products[0]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-orange-200 transition-all">
      {/* Banner */}
      <div className="relative h-36 overflow-hidden">
        <img src={factory.banner} alt={factory.name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-3 left-4 flex items-center gap-3">
          <img src={factory.avatar} alt={factory.name} className="w-12 h-12 rounded-xl border-2 border-white object-cover" />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-bold text-sm">{factory.name}</span>
              {factory.verified && (
                <span className="flex items-center gap-0.5 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  <i className="ri-shield-check-fill text-xs" /> Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-white/80 text-xs mt-0.5">
              <span>{factory.flag} {factory.city}</span>
              <span>·</span>
              <span>{factory.category}</span>
            </div>
          </div>
        </div>
        <div className="absolute top-3 right-3 bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
          MOQ ${factory.minOrderUSD.toLocaleString()}+
        </div>
      </div>

      <div className="p-5">
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { icon: "ri-star-fill", value: factory.rating, color: "text-yellow-500", label: "Rating" },
            { icon: "ri-shopping-bag-line", value: `${factory.totalOrders}+`, color: "text-orange-500", label: "Orders" },
            { icon: "ri-time-line", value: factory.responseTime, color: "text-green-500", label: "Response" },
            { icon: "ri-calendar-line", value: `${factory.yearsActive}yr`, color: "text-gray-500", label: "Experience" },
          ].map((s) => (
            <div key={s.label} className="text-center bg-gray-50 rounded-lg p-2">
              <div className={`w-5 h-5 flex items-center justify-center mx-auto mb-1`}>
                <i className={`${s.icon} ${s.color} text-sm`} />
              </div>
              <div className="text-xs font-bold text-gray-900 truncate">{s.value}</div>
              <div className="text-xs text-gray-400">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {factory.certifications.map((cert) => (
            <span key={cert} className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium">
              <i className="ri-checkbox-circle-fill mr-0.5" />{cert}
            </span>
          ))}
        </div>

        {/* Container Types */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-gray-500 font-medium">Containers:</span>
          {factory.containerTypes.map((ct) => (
            <span key={ct} className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full font-semibold">
              <i className="ri-truck-line mr-0.5" />{ct}
            </span>
          ))}
        </div>

        {/* Product Tabs */}
        {factory.products.length > 1 && (
          <div className="flex gap-1 mb-3 overflow-x-auto">
            {factory.products.map((p) => (
              <button
                key={p.id}
                onClick={() => setActiveProduct(p)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeProduct.id === p.id ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {p.title.split("—")[0].trim().split(" ").slice(0, 3).join(" ")}
              </button>
            ))}
          </div>
        )}

        {/* Active Product */}
        <div className="border border-gray-100 rounded-xl overflow-hidden mb-4">
          <div className="flex gap-3 p-3">
            <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
              <img src={activeProduct.image} alt={activeProduct.title} className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 line-clamp-2">{activeProduct.title}</p>
              {activeProduct.customizable && (
                <span className="inline-block text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full font-medium mt-1">
                  <i className="ri-edit-line mr-0.5" />Custom Branding
                </span>
              )}
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xs text-gray-400">Unit Price</p>
                  <p className="text-sm font-extrabold text-gray-900">${activeProduct.unitPrice.toFixed(2)}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-2">
                  <p className="text-xs text-gray-400">Min. Order</p>
                  <p className="text-sm font-extrabold text-orange-600">${activeProduct.moqUSD.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Container Qty */}
          <div className="border-t border-gray-50 px-3 py-2 bg-gray-50">
            <p className="text-xs text-gray-500 mb-1.5 font-medium">Units per container:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(activeProduct.containerQty).map(([type, qty]) => (
                <span key={type} className="text-xs bg-white border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full font-medium">
                  {type}: <strong>{qty.toLocaleString()} units</strong>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Lead Times */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-center">
            <i className="ri-flask-line text-green-500 text-lg block mb-1" />
            <p className="text-xs font-bold text-gray-900">Sample</p>
            <p className="text-xs text-green-600 font-semibold">{factory.sampleLeadTime}</p>
            <p className="text-xs text-gray-500">${factory.samplePrice} / sample</p>
          </div>
          <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 text-center">
            <i className="ri-ship-line text-orange-500 text-lg block mb-1" />
            <p className="text-xs font-bold text-gray-900">Bulk Order</p>
            <p className="text-xs text-orange-600 font-semibold">{factory.bulkLeadTime}</p>
            <p className="text-xs text-gray-500">MOQ ${factory.minOrderUSD.toLocaleString()}+</p>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onSampleOrder(factory, activeProduct)}
            className="flex-1 py-2.5 border-2 border-green-500 text-green-600 text-sm font-bold rounded-xl hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-flask-line mr-1" />Order Sample
          </button>
          <button
            onClick={() => onBulkOrder(factory, activeProduct)}
            className="flex-1 py-2.5 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-ship-line mr-1" />Bulk / Container
          </button>
        </div>

        {/* Expand about */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-3 text-xs text-gray-400 hover:text-gray-600 flex items-center justify-center gap-1 cursor-pointer"
        >
          {expanded ? "Hide details" : "About this factory"}
          <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
        </button>
        {expanded && (
          <p className="text-xs text-gray-600 leading-relaxed mt-2 pt-2 border-t border-gray-50">{factory.about}</p>
        )}
      </div>
    </div>
  );
}
